import { Box, Button, Modal, Tooltip, Typography, IconButton, CircularProgress } from '@mui/material'
import React, { Component } from 'react';
import { ImagePaths } from '../../Utility/ImagePaths';
import "../../Modules/FrontOffice/BillingTransaction/Billings.css";
import { withTranslation } from 'react-i18next';
import ToastMsg from '../ToastMsg/ToastMsg';
import RestAPIService from '../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../Utility/API/Serviceurls';
import { localGetItem, setCachevalue } from '../../Utility/Services/CacheProviderService';
import { ReasonPopup } from './CommonComponents';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
 
// Set the worker source for pdfjs using CDN to avoid loader issues
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// PDF Viewer component using pdfjs-dist - All pages scrollable with zoom
class PdfViewer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            numPages: 0,
            loading: true,
            error: null,
            scale: 0.5,
            renderedPages: []
        };
        this.containerRef = React.createRef();
        this.canvasRefs = [];
        this.pdfDoc = null;
    }

    componentDidMount() {
        this.loadPdf();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.src !== this.props.src) {
            this.loadPdf();
        }
        // Re-render all pages when scale changes
        if (prevState.scale !== this.state.scale && this.pdfDoc) {
            this.renderAllPages();
        }
        // Call onLoad when loading completes
        if (prevState.loading && !this.state.loading && this.props.onLoad) {
            this.props.onLoad();
        }
    }

    componentWillUnmount() {
        if (this.pdfDoc) {
            this.pdfDoc.destroy();
        }
    }

    loadPdf = async () => {
        const { src } = this.props;
        if (!src) return;

        this.setState({ loading: true, error: null, renderedPages: [] });

        try {
            const loadingTask = pdfjsLib.getDocument(src);
            this.pdfDoc = await loadingTask.promise;
            
            // Create refs for all pages
            this.canvasRefs = Array(this.pdfDoc.numPages).fill(null).map(() => React.createRef());
            
            this.setState({ 
                numPages: this.pdfDoc.numPages, 
                loading: false
            }, () => {
                this.renderAllPages();
            });
        } catch (error) {
            console.error('Error loading PDF:', error);
            this.setState({ loading: false, error: 'Failed to load PDF' });
        }
    };

    renderAllPages = async () => {
        if (!this.pdfDoc) return;

        const { numPages, scale } = this.state;
        
        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
            setTimeout(() => this.renderPage(pageNum, scale), (pageNum - 1) * 50);
        }
    };

    renderPage = async (pageNum, scale) => {
        if (!this.pdfDoc) return;

        const canvasRef = this.canvasRefs[pageNum - 1];
        if (!canvasRef || !canvasRef.current) return;

        try {
            const page = await this.pdfDoc.getPage(pageNum);
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            
            const viewport = page.getViewport({ scale });
            canvas.height = viewport.height;
            canvas.width = viewport.width;

            const renderContext = {
                canvasContext: context,
                viewport: viewport
            };

            await page.render(renderContext).promise;
        } catch (error) {
            console.error('Error rendering page:', pageNum, error);
        }
    };

    zoomIn = () => {
        this.setState(prev => ({ scale: Math.min(prev.scale + 0.25, 3) }));
    };

    zoomOut = () => {
        this.setState(prev => ({ scale: Math.max(prev.scale - 0.25, 0.25) }));
    };

    downloadPdf = () => {
        const { src, fileName = 'document.pdf' } = this.props;
        if (src) {
            const link = document.createElement('a');
            link.href = src;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    render() {
        const { src } = this.props;
        const { numPages, loading, error, scale } = this.state;

        if (!src) return null;

        if (loading) {
            return (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#525659' }}>
                    <CircularProgress sx={{ color: '#04B7B1' }} />
                </Box>
            );
        }

        if (error) {
            return (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', gap: 2 }}>
                    <Typography color="error">{error}</Typography>
                    <Button 
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={this.downloadPdf}
                        sx={{ backgroundColor: '#04B7B1', '&:hover': { backgroundColor: '#039b96' } }}
                    >
                        Download PDF Instead
                    </Button>
                </Box>
            );
        }

        return (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Toolbar */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '8px 12px', 
                    backgroundColor: '#f5f5f5',
                    borderBottom: '1px solid #e0e0e0',
                    flexWrap: 'wrap',
                    gap: 1
                }}>
                    {/* Page Count */}
                    <Typography variant="body2" sx={{ color: '#666' }}>
                        {numPages} {numPages === 1 ? 'Page' : 'Pages'}
                    </Typography>

                    {/* Zoom Controls */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton 
                            size="small" 
                            onClick={this.zoomOut} 
                            disabled={scale <= 0.5}
                            sx={{ color: '#04B7B1' }}
                        >
                            <ZoomOutIcon />
                        </IconButton>
                        <Typography variant="body2" sx={{ minWidth: '50px', textAlign: 'center' }}>
                            {Math.round(scale * 100)}%
                        </Typography>
                        <IconButton 
                            size="small" 
                            onClick={this.zoomIn} 
                            disabled={scale >= 3}
                            sx={{ color: '#04B7B1' }}
                        >
                            <ZoomInIcon />
                        </IconButton>
                    </Box>
                    
                    {/* Download Button */}
                    <Tooltip title="Download" arrow>
                        <IconButton 
                            size="small"
                            onClick={this.downloadPdf}
                            sx={{ 
                                backgroundColor: '#04B7B1', 
                                color: '#fff',
                                '&:hover': { backgroundColor: '#039b96' },
                                borderRadius: '4px',
                                padding: '6px'
                            }}
                        >
                            <DownloadIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* PDF Pages Container - Scrollable */}
                <Box 
                    ref={this.containerRef}
                    sx={{ 
                        flex: 1, 
                        overflow: 'auto', 
                        backgroundColor: '#525659',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '16px'
                    }}
                >
                    {/* Render all pages */}
                    {Array.from({ length: numPages }, (_, index) => (
                        <Box 
                            key={index}
                            sx={{
                                backgroundColor: '#fff',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                display: 'flex',
                                justifyContent: 'center'
                            }}
                        >
                            <canvas 
                                ref={this.canvasRefs[index]} 
                                style={{ display: 'block' }}
                            />
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }
}
class DocumentPreviewPop extends Component {
    constructor(props) {
        super(props);
        this.state = {
            Open: true,
            BillData: this.props.BillData,
            isCash: false,
            BillCancelClicked:false,
            previewItems: [],   // [{type:"image"/"pdf", url:"..."}]
            previewIndex: 0,
            pdfUrl: null,  // For single PDF preview from API
            itemLoading: {}  // {index: true/false}
        }
    }
    componentDidMount() {
        if (!this.props.Data && !this.props.ImageData) {
            this.getPrintPreview()
        } else if (this.props.Data && Array.isArray(this.props.Data) && this.props.Data.length > 0) {
            const loadingState = Object.fromEntries(this.props.Data.map((_, index) => [index, true]));
            this.setState({
                previewItems: this.props.Data || [],
                itemLoading: loadingState
            }, () => this.preloadImages())
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.Data !== this.props.Data) {
            const loadingState = Object.fromEntries((this.props.Data || []).map((_, index) => [index, true]));
            this.setState({ itemLoading: loadingState }, () => this.preloadImages());
        }
    }

    preloadImages = () => {
        this.state.previewItems.forEach((item, index) => {
            if (item.type === 'image') {
                setTimeout(() => {
                    const img = new Image();
                    img.src = item.url;
                    img.onload = () => this.setState(prev => ({ itemLoading: { ...prev.itemLoading, [index]: false } }));
                    img.onerror = () => this.setState(prev => ({ itemLoading: { ...prev.itemLoading, [index]: false } }));
                }, index * 1000); // 1 second delay per image to download sequentially
            }
            // PDFs load via PdfViewer onLoad
        });
    };

    errorMessage = (message) => {
        this.setState({
            isErrorMsg: true,
            isErrorMsgText: message
        })
    }

    successMessage = (message) => {
        this.setState({
            successMsg: true,
            successMsgText: message
        })
    }

    getPrintPreview = () => {
        try {
            let API_URL = ""
            if(this.props.PRINT_URL){
                API_URL = this.props.PRINT_URL
            }
            else if (this.props.BillData?.receipt_number) {
                let clinicID =  localGetItem("clinicId") !== null ? `&clinic_id=${localGetItem("clinicId")}`: ""
                let fromReservation =  this.props.BillData?.from_reservation ? `&from_reservation=${this.props.BillData?.from_reservation}`: ""
                API_URL = localGetItem("isNewBilling") === "true" 
                ? Serviceurls.NEW_RECEIPT_INDIV_PRINT + `?module_type=${this.props.Pharmacy ? "PHARMACY" : (this.props?.BillData?.receipt_typee || this.props?.BillData?.receipt_type)}&receipt_id=${this.props.BillData?.id}${clinicID}${fromReservation}${this.props.BillData?.from_safety_deposit ? "&is_safety_deposit=true" : ""}${this.props.BillData?.receipt_for === "Membership" ? "&is_membership_receipt=true" : ""}`
                : this.props.Pharmacy 
                  ? Serviceurls.RECEIPT_PHARMACY_PRINT + `?receipt_id=${this.props.BillData?.id}`
                  : Serviceurls.RECEIPT_INDIV_PRINT + `?receipt_id=${this.props.BillData?.id}${clinicID}${fromReservation}${this.props.BillData?.from_safety_deposit ? "&is_safety_deposit=true" : ""}`;
              
            }  else {
                let DocClinicID =  localGetItem("SelectedDoctorClinicId") !== null ? `&clinic_id=${localGetItem("SelectedDoctorClinicId")}`: ""
                if (this.props.BillData?.bill_types === "OT") {
                    API_URL = Serviceurls.GET_PRINT_PREVIEW + `?export=pdf&id=` + this.props.BillData?.id
                }
                else if (this.props.BillData?.bill_types === "In Patient") {
                    API_URL = ((localGetItem('isNewBilling') === "true") ? Serviceurls.NEW_IP_INVOICE_PRINT : Serviceurls.IP_INVOICE_PRINT) + `?ip_bill_summary_id=` + this.props.BillData?.id
                }
                else if (this.props.BillData?.bill_types === "Out Patient") {
                    API_URL = ((localGetItem('isNewBilling') === "true") ? Serviceurls.NEW_OP_INVOICE_PRINT : Serviceurls.OP_INVOICE_PRINT) + "?invoce_id=" + this.props.BillData?.id
                }
                else if (this.props.BillData?.bill_types === "pharmacy") {
                    API_URL = Serviceurls.PHARMACY_INVOICE_PRINT + "?id=" + this.props.BillData?.id + "&export=pdf" + "&pharmacy_id=" + this.props.BillData?.pharmacy_id + "";
                }
                else if (this.props.BillData?.bill_types === "Radiology") {
                    API_URL = ((localGetItem('isNewBilling') === "true") ? Serviceurls.NEW_RAD_BILL_POST : Serviceurls.RAD_BILL_POST) + '?bill_summary_id=' + this.props.BillData?.id + "&export_type=pdf"
                }
                else if (this.props.BillData?.bill_types === "Laboratory") {
                    // if(this.props.BillData?.bill_type === "cancelled"){ 
                        API_URL = ((localGetItem('isNewBilling') === "true") ? Serviceurls.NEW_LAB_CREATE_BILL_COMPLETED_PRINT  : Serviceurls.LAB_CREATE_BILL_COMPLETED_PRINT )  + "?bill_summary_id=" + this.props.BillData?.id + "&export_type=pdf" + "&laboratory_id=" + this.props.BillData?.laboratory_id + "";
                    // }
                    // else if (this.props.BillData?.payment_status === "paid") {
                    //     API_URL = Serviceurls.LAB_CREATE_BILL_COMPLETED_PRINT + "?bill_summary_id=" + this.props.BillData?.id + "&export_type=pdf" + "&laboratory_id=" + this.props.BillData?.laboratory_id + "";
                    // }
                    // else if (this.props.BillData?.payment_status === "not paid") {
                    //     API_URL = Serviceurls.LAB_CREATE_BILL_PARTIAL_PRINT + "?bill_summary_id=" + this.props.BillData?.id + "&export_type=pdf" + "&laboratory_id=" + this.props.BillData?.laboratory_id;
                    // }
                    
                }
                else if(this.props.BillData?.billing_status){
                    API_URL = Serviceurls.PHARMA_BILLS_GET + "?id=" + this.props.BillData?.id + "&export=pdf" + "&pharmacy_id=" + this.props.BillData?.pharmacy_id + "";
                }

                if(DocClinicID){
                    API_URL = API_URL + DocClinicID
                }
            }
            RestAPIService.getAllPrint(API_URL).
                then((response) => {
                    const file = new Blob(
                        [response.data],
                        { type: 'application/pdf' });
                    const fileURL = URL.createObjectURL(file);
                    this.setState({ pdfUrl: fileURL });
                }).catch((e) => {
                    this.errorMessage(e.message)
                })
        }
        catch (e) {
            this.errorMessage(e.message)
        }
    }
    handleClose = () => {
        this.setState({
            Open: false,
        })
        this.props.ClosePopUp()
    }

    CancelBill(reason) {
        try {
            let data = {
                bill_id: this.state.BillData?.id,
                bill_type: 'cancelled',
                cancel_reason: reason
            }
            let URL = this.state.BillData?.bill_types === "In Patient" ? Serviceurls.IP_RECEIVE_PAYMENT_GET : Serviceurls.FO_PATIEMT_BILLING
            RestAPIService.create(data, URL)
                .then((response) => {
                    if (response.data.status === "success") {
                        this.props.ClosePopUp(true,response.data.message )
                    }
                }).catch((error) => {
                    if (error?.response?.data?.status === "fail") {
                        this.errorMessage(error.response.data.message)
                      } else {
                        this.errorMessage(error.message)
                      }
                })
        }
        catch (e) {

        }
    }

    GetAppointmentDetails(id, billid) {
        try {
            const { history } = this.props;
            RestAPIService.getAll(Serviceurls.FO_GET_APPOINMENT_DETAILS + "?appointment_id=" + id).
                then((response) => {
                    if (response.data.status === "success") {
                        this.setState({
                            AppointmentDetails: response.data.data
                        }, () => {
                            setCachevalue(JSON.stringify(this.state.AppointmentDetails), "patientData")
                            history.push({ pathname: "/OPHome/PatientDetails/Billing", state: { AppointmentID: id, InvoiceID: billid } })
                        })
                    }
                    else {
                        this.errorMessage(response.data.message)
                    }
                }).catch((error) => {
                    this.errorMessage(error.message)
                })
        }
        catch (e) {
            this.errorMessage(e.message)
        }
    }

    GetIPAppointmentDetails(id, billid) {
        try {
            const { history } = this.props;
            RestAPIService.getAll(Serviceurls.FO_GET_IP_ADMISSION_DETAILS + "?ip_admission_id=" + id).
                then((response) => {
                    if (response.data.status === "success") {
                        this.setState({
                            AppointmentDetails: response.data.data
                        }, () => {
                                setCachevalue(JSON.stringify(this.state.AppointmentDetails), "IppatientData")
                                history.push({ pathname: "/IPHome/PatientDetails/ServiceBilling", state: { AppointmentID: id, InvoiceID: billid } })
                            })
                    }
                    else {
                        this.errorMessage(response.data.message)
                    }
                }).catch((error) => {
                    this.errorMessage(error.message)
                })
        }
        catch (e) {
            this.errorMessage(e.message)
        }
    }

    addCancelComments = (comments) =>{
        this.CancelBill(comments)
    }
    popupClose = () => {
        this.setState({
          BillCancelClicked: false,
        })
      }
      msgClose(){
        this.setState({
          isErrorMsg: false,
          isErrorMsgText: '',
          successMsg: false,
          successMsgText: ''
        })
      }

    goPrev = () => {
        this.setState(prev => ({
            previewIndex: Math.max(prev.previewIndex - 1, 0)
        }));
    };

    goNext = () => {
        this.setState(prev => ({
            previewIndex: Math.min(prev.previewIndex + 1, prev.previewItems.length - 1)
        }));
    };

    render() {
        const { t } = this.props
        return (
            <div>
                <Modal
                    open={this.state.Open}
                >
                    <Box component={'div'} className="eMed_BillPreview_Main" >
                        <Box component={'div'} className="eMed_BillPreview_Top">
                            <Typography variant='h6'>{this.props.Title}
                                {/* Show count + name ONLY when array preview is used */}
                                {Array.isArray(this.state.previewItems) && this.state.previewItems?.length > 0 && (
                                    <span style={{ fontSize: "0.85vw", marginLeft: "0.625vw" }}>
                                        {" "}
                                        ({this.state.previewIndex + 1}/{this.state.previewItems?.length})
                                        {" - "}
                                        {this.state.previewItems[this.state.previewIndex]?.name ?? ""}
                                    </span>
                                )}
                            </Typography>
                            <Button onClick={() => { this.handleClose() }} >
                                <img src={ImagePaths.LabCloseButton.default} alt="close" className='eMed_action_img_small' />
                            </Button>
                        </Box>
                        <Box component={'div'} className="eMed_BillPreview_Image" position="relative" sx={{ height:"36vw"}}>
                            {/* If Data is array → navigation + preview */}
                            {(Array.isArray(this.state.previewItems) && this.state.previewItems?.length > 0) ? (
                                <>
                                    {/* Navigation Buttons */}
                                    {this.state.previewItems?.length > 1 && (
                                        <>
                                            <IconButton
                                                onClick={this.goPrev}
                                                sx={{ position: "absolute", left: 10, top: "50%", zIndex: 10 }}
                                            >
                                                <ChevronLeftIcon sx={{ fontSize: "1.8vw", color: "#04B7B1", stroke: "#04B7B1", strokeWidth: 2 }}/>
                                            </IconButton>

                                            <IconButton
                                                onClick={this.goNext}
                                                sx={{ position: "absolute", right: 10, top: "50%", zIndex: 10 }}
                                            >
                                                <ChevronRightIcon sx={{ fontSize: "1.8vw", color: "#04B7B1", stroke: "#04B7B1", strokeWidth: 2 }}/>
                                            </IconButton>
                                        </>
                                    )}

                                    {/* Preview of all items - rendered but hidden except current for preloading */}
                                    {this.state.previewItems?.map((item, index) => (
                                        <Box 
                                            key={index} 
                                            sx={{ 
                                                display: index === this.state.previewIndex ? 'block' : 'none', 
                                                position: 'relative', 
                                                width: '100%', 
                                                height: '100%' 
                                            }}
                                        >
                                            {this.state.itemLoading[index] && (
                                                <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, backgroundColor: 'rgba(255,255,255,0.8)' }}>
                                                    <CircularProgress sx={{ color: '#04B7B1' }} />
                                                </Box>
                                            )}
                                            {item?.type === "image" ? (
                                                this.state.itemLoading[index] ? null : (
                                                    <img
                                                        src={item.url}
                                                        width="100%"
                                                        height="100%"
                                                        style={{ objectFit: "contain" }}
                                                        alt="preview"
                                                    />
                                                )
                                            ) : (
                                                <PdfViewer
                                                    key = {`${item?.name}_${index}`}
                                                    src={item?.url}
                                                    fileName={item?.name || 'document.pdf'}
                                                    onLoad={() => this.setState(prev => ({ itemLoading: { ...prev.itemLoading, [index]: false } }))}
                                                />
                                            )}
                                        </Box>
                                    ))}
                                </>
                            ) : this.props.ImageData ? (
                                <img src={this.props.ImageData} width="100%" height="100%" style={{ objectFit: "contain" }} alt="preview"/>
                            ) : (<PdfViewer src={this.props.Data ? this.props.Data : this.state.pdfUrl} />
                            )}
                        </Box>
                        <Box component={'div'} className="eMed_BillPreview_BtnDiv">
                            {/* {
                                (this.props.cancelBillButton && this.props.BillData?.bill_types !== "pharmacy" && this.props.BillData?.bill_types !== "laboratory") ?
                                    <Button variant='outlined' size='small' color='error' className='eMed_Filter_Btns' onClick={() => { this.setState({ BillCancelClicked: true }) }}>
                                        {t("CancelBill")}
                                    </Button> : null
                            } */}
                            {/* {
                                this.props.ModifyBillButton ?
                                    <Button variant='contained' size='small' className='eMed_Filter_Btns' onClick={() => {
                                        if(this.state.BillData?.bill_types === "Out Patient"){
                                        this.GetAppointmentDetails(this.state.BillData?.appointment_id, this.state.BillData?.id)
                                        }
                                        if(this.state.BillData?.bill_types === "In Patient"){
                                            this.GetIPAppointmentDetails(this.state.BillData?.ip_number_id, this.state.BillData?.id)
                                        }
                                    }}>
                                        {t("ModifyBill")}
                                    </Button> : null
                            } */}
                            {
                                this.props.PrintButton ?
                                    <Tooltip title={t("Print")} placement="top" arrow>
                                        <Button onClick={() => { this.handleClose() }} className='eMed_usrconf_btn'>
                                            <img src={ImagePaths.PrintIcons.default} alt="print" className='eMed_action_img' />
                                        </Button>
                                    </Tooltip> : null
                            }
                        </Box>
                    </Box>
                </Modal>
                {this.state.isErrorMsg ?
                    <ToastMsg
                        severity={'error'}
                        msg={this.state.isErrorMsgText}
                        msgPop={this.msgClose.bind(this)}
                    />
                    : null}
                {this.state.successMsg ?
                    <ToastMsg
                        severity={'success'}
                        msg={this.state.successMsgText}
                        msgPop={this.msgClose.bind(this)}
                    />
                    : null}
                {this.state.BillCancelClicked ? <ReasonPopup
                    title={"Are you sure? Do you want to cancel the Bill"}
                    AlertPopupClose={this.popupClose.bind(this)}
                    label={"Enter the Cancel Reason"}
                    btntext={'Cancel'}
                    btnvarient={'outlined'}
                    btncolor={'error'}
                    btntext1={'Confirm'}
                    btnvarient1={'contained'}
                    sendCmt={this.addCancelComments.bind(this)} /> : null}
            </div>
        )
    }
}
export default withTranslation()(DocumentPreviewPop)